import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Compass } from "lucide-react";
import api from "../lib/api";

const PILLAR_IMAGES = {
  "renew-mind": "https://images.unsplash.com/photo-1569360556894-15dca0c6ff1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxqb3VybmFsaW5nJTIwY29mZmVlJTIwbW9ybmluZ3xlbnwwfHx8fDE3ODA5MTI2MDR8MA&ixlib=rb-4.1.0&q=85",
  "guard-heart": "https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxyZWFkaW5nJTIwYmlibGUlMjBsaWdodHxlbnwwfHx8fDE3ODA5MTI2MDR8MA&ixlib=rb-4.1.0&q=85",
  "trust-surrender": "https://images.unsplash.com/photo-1772878491391-6b1d79349d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjaHVyY2glMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODA5MTI2MTZ8MA&ixlib=rb-4.1.0&q=85",
};
const PILLAR_ICONS = { "renew-mind": Sparkles, "guard-heart": Heart, "trust-surrender": Compass };

export default function Landing() {
  const [pillars, setPillars] = useState([]);
  const [price, setPrice] = useState(49);

  useEffect(() => {
    api.get("/course/overview").then((r) => {
      setPillars(r.data.pillars);
      setPrice(r.data.price_usd);
    }).catch(() => {});
  }, []);

  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 slow-pan"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1534171472159-edb6d1e0b63c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdhcm0lMjBsaWdodCUyMHNhbmQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MDkxMjYxNnww&ixlib=rb-4.1.0&q=85)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.5,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/40 via-[#FDFBF7]/70 to-[#FDFBF7]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-28 sm:py-40">
          <div className="max-w-3xl">
            <div className="fade-up text-xs uppercase tracking-[0.25em] text-[#A36B5E] mb-6" data-testid="hero-tag">
              For creators, communicators & quiet voices alike
            </div>
            <h1
              className="fade-up delay-100 font-display text-5xl sm:text-6xl md:text-7xl text-[#2D2A26] leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              Align your voice
              <span className="block italic text-[#A36B5E]">with your calling.</span>
            </h1>
            <p className="fade-up delay-200 mt-8 text-lg sm:text-xl text-[#5C5852] leading-relaxed max-w-2xl">
              A faith-based course for anyone learning to live aligned in a connected world —
              whether you create for an audience of millions on Instagram, YouTube, TikTok, or Facebook,
              pastor a small community, or simply post quietly from where you are. Rooted in
              <span className="italic"> Romans 12:2, Proverbs 4:23, and Proverbs 3:5–6</span> —
              three Scriptures that quietly rewire how you show up, online and off.
            </p>
            <div className="fade-up delay-300 mt-10 flex flex-wrap gap-4">
              <Link to="/assessment" data-testid="hero-cta-assessment" className="btn-primary rounded-full px-8 py-4 inline-flex items-center gap-2 font-medium">
                Take the Assessment <ArrowRight size={18} />
              </Link>
              <Link to="/curriculum" data-testid="hero-cta-curriculum" className="btn-outline-clay rounded-full px-8 py-4 inline-flex items-center gap-2 font-medium">
                Preview the Course
              </Link>
            </div>
            <div className="fade-up delay-400 mt-12 text-sm text-[#8A857D] italic max-w-md">
              "Do not conform to the pattern of this world, but be transformed by the renewing of your mind."
              <span className="block not-italic mt-1 text-xs tracking-widest uppercase">— Romans 12:2</span>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-24 sm:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-[#8A857D]">The Curriculum</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2D2A26] mt-3 tracking-tight">
              Three pillars. One transformation.
            </h2>
            <p className="mt-5 text-[#5C5852] text-lg leading-relaxed">
              Each pillar is rooted in a single Scripture and unfolds across three lessons —
              with scripture, reflection journaling, and <em>The Inner Room</em> — a quiet place to think it through.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((p, idx) => {
              const Icon = PILLAR_ICONS[p.id] || Sparkles;
              return (
                <div
                  key={p.id}
                  className="group bg-white border border-[#EAE5D9] rounded-2xl overflow-hidden transition-all hover:-translate-y-2 hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.1)] fade-up"
                  style={{ animationDelay: `${idx * 0.15}s` }}
                  data-testid={`pillar-card-${p.id}`}
                >
                  <div
                    className="h-56 bg-cover bg-center"
                    style={{ backgroundImage: `url(${PILLAR_IMAGES[p.id]})` }}
                  />
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#F5F2EB] flex items-center justify-center">
                        <Icon size={18} className="text-[#A36B5E]" />
                      </div>
                      <div className="text-xs uppercase tracking-widest text-[#8A857D]">{p.scripture_ref}</div>
                    </div>
                    <h3 className="font-display text-2xl text-[#2D2A26] mb-3">{p.title}</h3>
                    <p className="text-[#5C5852] text-sm leading-relaxed mb-5">{p.summary}</p>
                    <div className="text-xs text-[#8A857D]">{p.lessons.length} lessons</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ASSESSMENT TEASER */}
      <section className="py-24 sm:py-32 bg-[#F5F2EB]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#A36B5E] mb-6">The Divine Alignment Continuum</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#2D2A26] tracking-tight">
            Where are you on the journey?
          </h2>
          <p className="mt-6 text-lg text-[#5C5852] max-w-2xl mx-auto leading-relaxed">
            A 10-question assessment that places you on the alignment continuum and shows where you have the most to gain.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm">
            {["Seeking", "Awakening", "Aligned", "Anointed"].map((s, i) => (
              <React.Fragment key={s}>
                <div className="text-[#2D2A26] font-medium tracking-wide">{s}</div>
                {i < 3 && <div className="w-8 sm:w-16 h-px bg-[#C49A7E]" />}
              </React.Fragment>
            ))}
          </div>
          <Link
            to="/assessment"
            data-testid="assessment-teaser-cta"
            className="btn-primary rounded-full px-8 py-4 mt-12 inline-flex items-center gap-2 font-medium"
          >
            Take the 5-minute Assessment <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* SPEAKER / FOUNDER */}
      <section className="py-24 sm:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-2 gap-16 items-center">
          <div
            className="aspect-[4/5] bg-cover bg-center rounded-2xl"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1504052434569-70ad5836ab65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxyZWFkaW5nJTIwYmlibGUlMjBsaWdodHxlbnwwfHx8fDE3ODA5MTI2MDR8MA&ixlib=rb-4.1.0&q=85)` }}
          />
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#8A857D]">Speaker Engagement</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2D2A26] mt-3 tracking-tight">
              Invite the founder to your stage.
            </h2>
            <p className="mt-6 text-lg text-[#5C5852] leading-relaxed">
              Beyond the course, I'd love to meet your community in person — keynotes, retreats, podcasts,
              creator gatherings. Send a request and we'll start the conversation.
            </p>
            <Link
              to="/speaker"
              data-testid="speaker-cta"
              className="btn-outline-clay rounded-full px-8 py-4 mt-8 inline-flex items-center gap-2 font-medium"
            >
              Request a booking <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 sm:py-32 bg-[#EAE5D9]/40">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#A36B5E] mb-6">Enrollment</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#2D2A26] tracking-tight">
            One investment. Lifetime access.
          </h2>
          <div className="mt-12 bg-white rounded-2xl border border-[#EAE5D9] p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="font-display text-6xl text-[#2D2A26]">${price}</div>
            <div className="text-sm text-[#8A857D] mt-1">one-time · lifetime access</div>
            <ul className="mt-8 space-y-3 text-left max-w-md mx-auto text-[#5C5852]">
              <li className="flex gap-3"><span className="text-[#5F6F52]">✓</span> 9 lessons across 3 Scripture pillars</li>
              <li className="flex gap-3"><span className="text-[#5F6F52]">✓</span> Personal reflection journal</li>
              <li className="flex gap-3"><span className="text-[#5F6F52]">✓</span> The Inner Room — a quiet, scripture-anchored reflection space</li>
              <li className="flex gap-3"><span className="text-[#5F6F52]">✓</span> Alignment Assessment + progress tracking</li>
              <li className="flex gap-3"><span className="text-[#5F6F52]">✓</span> Free preview lesson — start before you pay</li>
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register"
                data-testid="pricing-cta-enroll"
                className="btn-primary rounded-full px-8 py-4 font-medium"
              >
                Begin Free Preview
              </Link>
              <Link
                to="/pricing"
                data-testid="pricing-cta-details"
                className="btn-outline-clay rounded-full px-8 py-4 font-medium"
              >
                See full details
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
